import { memo } from 'react';
import { Box, Group, Text, Input } from '@mantine/core';
import {
  IconUser,
  IconAlertTriangle,
  IconFlag,
  IconCalendar,
  IconCalendarEvent,
  IconCalendarCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { FormerMemberBadge } from '@/components/common/FormerMemberBadge';
import {
  InlineStatusMenu,
  type InlineStatusOption,
} from '@/components/common/InlineStatusMenu';
import { UserAvatar } from '@/components/user/UserAvatar';
import { BasicInfoFieldsProps } from '../TaskGeneralInfo.types';
import {
  TaskStatus,
  STATUS_COLORS,
  PRIORITY_COLORS,
} from '../../../../../types';
import { useLocalizedDate } from '../../../../../hooks/useLocalizedDate';
import { formatTaskStatus } from '../../../../../utils/taskHelpers';
import { translatePriority } from '../../../../../utils/enumTranslation';
import { ICON_SIZE_INFO_LABEL } from '../constants';
import styles from '../TaskGeneralInfo.module.css';

const STATUS_OPTIONS = [
  { status: TaskStatus.Pending, labelKey: 'common.pending' },
  { status: TaskStatus.InProgress, labelKey: 'actions.progress' },
  { status: TaskStatus.Delayed, labelKey: 'actions.delayed' },
  { status: TaskStatus.Completed, labelKey: 'actions.done' },
  { status: TaskStatus.Archived, labelKey: 'taskStatus.archived' },
] as const;

export const BasicInfoFields = memo(function BasicInfoFields({
  task,
  onStatusChange,
  readOnly = false,
}: BasicInfoFieldsProps) {
  const { t } = useTranslation();
  const { formatDateCompact, isOverdue } = useLocalizedDate();

  const statusLabel = (
    <Text size="sm" c={STATUS_COLORS[task.status]} className={styles.infoValue}>
      {formatTaskStatus(task.status, t)}
    </Text>
  );

  const statusOptions: InlineStatusOption<TaskStatus>[] = STATUS_OPTIONS.map(
    ({ status, labelKey }) => ({
      value: status,
      label: t(labelKey),
      active: task.status === status,
    })
  );

  return (
    <Box className={styles.basicInfo}>
      <Box className={styles.infoGrid}>
        {task.assignedToName != null && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconUser size={ICON_SIZE_INFO_LABEL} />
              <Input.Label>{t('fields.assignee')}</Input.Label>
            </Group>
            <Group gap="xs" align="center">
              <UserAvatar
                displayName={task.assignedToName}
                avatarUrl={task.assignedToAvatarUrl}
                size="sm"
              />
              <Text size="sm" className={styles.infoValue}>
                {task.assignedToName}
                {task.assignedToIsFormerMember && <FormerMemberBadge />}
              </Text>
            </Group>
          </Box>
        )}

        {/* Status with Inline Menu */}
        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconAlertTriangle size={ICON_SIZE_INFO_LABEL} />
            <Input.Label>{t('common.status')}</Input.Label>
          </Group>
          <InlineStatusMenu
            triggerLabel={statusLabel}
            options={statusOptions}
            onSelect={onStatusChange}
            readOnly={readOnly}
          />
        </Box>

        {/* Priority */}
        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconFlag size={ICON_SIZE_INFO_LABEL} />
            <Input.Label>{t('common.priority')}</Input.Label>
          </Group>
          <Text
            size="sm"
            c={PRIORITY_COLORS[task.priority]}
            className={styles.infoValue}
          >
            {translatePriority(task.priority, t)}
          </Text>
        </Box>

        {/* Start Date - Only show if set */}
        {task.startDate && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendarEvent size={ICON_SIZE_INFO_LABEL} />
              <Input.Label>{t('fields.startDate')}</Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue}>
              {formatDateCompact(task.startDate)}
            </Text>
          </Box>
        )}

        {/* Due Date - Only show if set */}
        {task.dueDate && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendar size={ICON_SIZE_INFO_LABEL} />
              <Input.Label>{t('fields.dueDate')}</Input.Label>
            </Group>
            <Text
              size="sm"
              className={styles.infoValue}
              c={
                isOverdue(task.dueDate) && task.status !== TaskStatus.Completed
                  ? 'red'
                  : undefined
              }
            >
              {formatDateCompact(task.dueDate)}
            </Text>
          </Box>
        )}

        {/* Completed At - Only show when task is completed */}
        {task.completedAt && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendarCheck size={ICON_SIZE_INFO_LABEL} />
              <Input.Label>{t('fields.completedAt')}</Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue} c="green">
              {formatDateCompact(task.completedAt)}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
});
